import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Heart, Mail, Globe, Users, ExternalLink, Instagram } from 'lucide-react';

const OngsPage = () => {
  const [parceiros, setParceiros] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchParceiros = async () => {
      try {
        const response = await axios.get(`${API}/ongs`);
        setParceiros(response.data);
      } catch (error) {
        console.error('Error fetching parceiros:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchParceiros();
  }, []);

  const tipoLabels = {
    ong: 'ONG',
    empresa: 'Empresa'
  };

  const tipoColors = {
    ong: 'bg-[#E57373]',
    empresa: 'bg-[#64B5F6]'
  };

  return (
    <div className="min-h-screen bg-[#F5D79C]">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="logo-container w-20 h-20 mx-auto flex items-center justify-center mb-4">
              <Heart className="w-10 h-10 text-[#E57373]" />
            </div>
            <h1 className="text-4xl font-display font-bold text-[#5D4A3A] mb-4">Nossos Parceiros</h1>
            <p className="text-[#7A5B3E] max-w-2xl mx-auto">
              Conheça as ONGs e empresas que apoiam a causa animal e ajudam o MiauCãoMigo a fazer a diferença!
            </p>
          </div>

          {/* Cards */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-[#FCEBC5] rounded-2xl h-64 animate-pulse border border-[#C4956A]"></div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {parceiros.map(p => (
                <div 
                  key={p.id} 
                  className="bg-[#FCEBC5] rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 border border-[#C4956A]"
                  data-testid={`parceiro-card-${p.id}`}
                >
                  {/* Header */}
                  <div className="bg-[#6B5344] p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-14 h-14 bg-[#F5D79C]/20 rounded-full flex items-center justify-center">
                        <Users className="w-7 h-7 text-[#FCEBC5]" />
                      </div>
                      {p.tipo && (
                        <span className={`${tipoColors[p.tipo]} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
                          {tipoLabels[p.tipo]}
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-[#FCEBC5]">{p.nome}</h3>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-4">
                    <p className="text-[#7A5B3E] text-sm">{p.descricao}</p>

                    <div className="flex flex-col gap-2">
                      {p.instagram && (
                        <a 
                          href={p.instagram} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-[#6B5344] hover:text-[#5D4A3A] font-medium"
                        >
                          <Instagram className="w-4 h-4" />
                          Instagram
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}

                      {p.website && (
                        <a 
                          href={p.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <Button className="w-full btn-primary">
                            <Globe className="w-4 h-4 mr-2" />
                            Visitar Site
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 bg-[#6B5344] rounded-2xl p-8 md:p-12 text-center">
            <Heart className="w-12 h-12 mx-auto mb-4 text-[#FCEBC5]" />
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 text-[#FCEBC5]">
              Quer se tornar um parceiro?
            </h2>
            <p className="text-[#F5E6C8] max-w-2xl mx-auto mb-6">
              Você também pode ajudar! Alimente o animal mais próximo da sua casa, ajude a encontrar um lar para ele. 
              Qualquer dúvida, entre em contato com a gente!
            </p>
            <a href="mailto:miaucaomigo@gmail.com">
              <Button 
                size="lg" 
                className="bg-[#F5D79C] text-[#5D4A3A] hover:bg-[#FCEBC5] rounded-full px-8"
              >
                <Mail className="w-5 h-5 mr-2" />
                miaucaomigo@gmail.com
              </Button>
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OngsPage;
